import { IBridgeDevice } from '@principle-theorem/principle-core/interfaces';
import { omitEmptyValues, serialise } from '@principle-theorem/shared';
import { DatabaseReference, child, push, set } from 'firebase/database';
import { gte, valid } from 'semver';
import { CloudToBridgeCommand } from './integrations/commands';
import { IBaseCommand, IDeviceCommand } from './send-command-to-device';
import { DEVICE_SETTINGS_PATH, PrincipleSettingsUpdate } from './settings';

export const SETTINGS_NODE_MIN_BRIDGE_VERSION = '1.266.0';

/**
 * Bridges in the field update on their own schedule, so the cloud must keep
 * talking to bridges released before the settings node existed.
 * A device that has never reported a valid version is treated as one of those.
 */
export class BridgeVersion {
  static supportsSettingsNode(device: IBridgeDevice): boolean {
    return BridgeVersion._isAtLeast(device, SETTINGS_NODE_MIN_BRIDGE_VERSION);
  }

  static async sendSettings(
    device: IBridgeDevice,
    deviceDatabaseRef: DatabaseReference,
    commandContext: Omit<IDeviceCommand, 'command'>,
    settings: Partial<PrincipleSettingsUpdate>
  ): Promise<void> {
    const pendingSettings = omitEmptyValues(settings);
    if (BridgeVersion.supportsSettingsNode(device)) {
      await set(
        child(deviceDatabaseRef, DEVICE_SETTINGS_PATH),
        pendingSettings
      );
      return;
    }
    await BridgeVersion._sendLegacyUpdateSettings(
      deviceDatabaseRef,
      commandContext,
      pendingSettings
    );
  }

  private static _isAtLeast(
    device: IBridgeDevice,
    minimumVersion: string
  ): boolean {
    const version = valid(device.settings?.version);
    return !!version && gte(version, minimumVersion);
  }

  private static async _sendLegacyUpdateSettings(
    deviceDatabaseRef: DatabaseReference,
    commandContext: Omit<IDeviceCommand, 'command'>,
    settings: Partial<PrincipleSettingsUpdate>
  ): Promise<void> {
    const command: IBaseCommand<
      CloudToBridgeCommand.UpdateSettings,
      Partial<PrincipleSettingsUpdate>
    > = {
      type: CloudToBridgeCommand.UpdateSettings,
      data: settings,
    };
    const deviceCommand: IDeviceCommand = { ...commandContext, command };
    const commandRef = push(child(deviceDatabaseRef, 'commands'));
    await set(commandRef, serialise(deviceCommand));
  }
}
